import { noop } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import * as v from "valibot"

import { $rqCreateApiClient } from "@/api/create"
import { $rqIotApiClient } from "@/api/iot"
import { getXOrganizationHeaders } from "@/api/utils"
import { DEVICEGRAPH_TIMEFRAME } from "@/components/graphing/DeviceStatusGraph/constants"
import type { ThingPageProps } from "@/components/shared/ThingSidebar"
import { TRIGGER_NEW_ID } from "@/features/triggerBuilder/context"
import { routeGuard } from "@/lib/routing/beforeLoad"

const popupSchema = v.fallback(
  v.optional(v.union([v.literal("info"), v.literal("dashboard")])),
  undefined
) satisfies v.GenericSchema<NonNullable<ThingPageProps[`activeElements`]>[`popup`] | undefined>

const graphSchema = v.fallback(
  v.optional(
    v.union(Object.keys(DEVICEGRAPH_TIMEFRAME).map(key => v.literal(key as keyof typeof DEVICEGRAPH_TIMEFRAME)))
  ),
  undefined
)

/**
 * `triggerId` only makes sense alongside `tab: "triggers"` (it selects which trigger opens in the
 * embedded editor) — modeled as a discriminated union so that illegal combinations (e.g. a
 * `triggerId` while on the "device" tab) are a type error, not just a runtime concern. An illegal
 * combination crafted by hand in the URL is simply stripped, since the matched branch doesn't
 * declare the field.
 *
 * Besides a real trigger's uuid, `triggerId` also accepts `TRIGGER_NEW_ID` — the in-progress
 * "create new" draft, which has no server id yet. Routing it through the URL like any other
 * selection means switching to/from it goes through the same navigation (and unsaved-changes
 * guard) as switching between real triggers.
 */
const searchSchema = v.fallback(
  v.variant("tab", [
    v.object({
      tab: v.literal("triggers"),
      triggerId: v.fallback(v.optional(v.union([v.pipe(v.string(), v.uuid()), v.literal(TRIGGER_NEW_ID)])), undefined),
      popup: popupSchema,
      graph: graphSchema
    }),
    v.object({ tab: v.literal("device"), popup: popupSchema, graph: graphSchema }),
    v.object({ tab: v.literal("sketch"), popup: popupSchema, graph: graphSchema }),
    v.object({ tab: v.literal("variables"), popup: popupSchema, graph: graphSchema })
  ]),
  { tab: "variables" }
)

export const Route = createFileRoute("/_authlayout/_header/things/$id/")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context }) => {
    await routeGuard(context)
  },
  loader: async ({ context: { queryClient, spaceStorage, thingQueryOptions }, params }) => {
    const space = spaceStorage.getSpace()

    // A bare `query()`, not `staleTime: "static"`: the `$id` route validates
    // with the same key, so this dedupes onto its in-flight fetch instead of
    // resolving to a stale copy. The rejection is swallowed on purpose — that route owns
    // validation, and an error escaping here would surface a raw API error ahead
    // of its `notFound()`.
    const thing = await queryClient.query(thingQueryOptions).catch(() => undefined)
    if (!thing) return

    // Thing properties and property types are already prefetched by the parent route's
    // beforeLoad (they don't depend on the thing data, only on params/space).

    // Prefetch thing's linked device data if applicable
    if (thing.device_id) {
      queryClient
        .query(
          $rqIotApiClient.queryOptions("get", `/iot/v2/devices/{id}`, {
            params: { header: getXOrganizationHeaders(space), path: { id: thing.device_id } }
          })
        )
        .catch(noop)
    }

    // Prefetch thing dashboards
    queryClient
      .query(
        $rqIotApiClient.queryOptions("get", `/iot/v3/dashboards`, {
          params: { header: getXOrganizationHeaders(space), query: { thing_id: params.id } }
        })
      )
      .catch(noop)

    // Prefetch sketch data if thing is linked to a sketch
    if (thing.sketch_id) {
      queryClient
        .query(
          $rqCreateApiClient.queryOptions("get", "/create/v2/sketches/byID/{id}", {
            params: {
              header: getXOrganizationHeaders(space),
              path: { id: thing.sketch_id }
            }
          })
        )
        .catch(noop)
    }
  }
})
